import { Check, Copy } from '@phosphor-icons/react'
import { useEffect, useRef, useState } from 'react'

import { CURRI_VERSION, useAuth } from '../../auth/AuthProvider.tsx'
import { CONSOLE } from '../../theme/console.ts'

import { useCyclingFeed } from './useCyclingFeed.ts'

// The hero's dark code console: the two-request cURL walkthrough, a mock
// response line, and a simulated webhook feed underneath.
//
// One language tab for now. The pill bar stays so more languages slot in when
// typed SDKs exist — until then the portal shows only requests a partner can
// actually make (see the INT-361 landing scope decisions).
//
// The feed's latencies are illustrative — fabricated like the snippet's
// addresses — but the names are the real partner-visible `status` values
// (content/graphql/webhooks-errors-statuses/statuses.md; emitted by the
// sender in packages/curri-delivery-webhooks/src/curri.ts). The webhook has no
// named event topics, so the feed cycles four statuses that tell a delivery's
// arc, with the same dots as the webhooks signed-out feed so the two screens
// agree.
const FEED_SOURCE = [
  { name: 'pending', dot: '#2563EB', meta: 'POST · 200 · 38 ms' },
  { name: 'en_route_to_origin', dot: '#F59E0B', meta: 'POST · 200 · 41 ms' },
  { name: 'en_route_to_destination', dot: '#7C5CFC', meta: 'POST · 200 · 33 ms' },
  { name: 'delivered', dot: '#05A468', meta: 'POST · 200 · 46 ms' },
] as const

interface FeedEvent {
  id: number
  name: string
  dot: string
  meta: string
  time: string
}

// Module-level on purpose: `useCyclingFeed` takes it as an effect dependency.
const makeFeedEvent = (index: number): Omit<FeedEvent, 'id'> => ({
  ...FEED_SOURCE[index % FEED_SOURCE.length],
  time: new Date().toLocaleTimeString('en-US', { hour12: false }),
})

// The copyable text mirrors the rendered snippet exactly — same requests, same
// live version header — so what lands in a terminal is what the page showed.
const curlSnippet = (
  baseUrl: string
) => `# Create a payload — quotes come back embedded
curl ${baseUrl}/api/payloads \\
  --request POST \\
  --header 'Curri-Version: ${CURRI_VERSION}' \\
  --header 'Authorization: Bearer $CURRI_TOKEN' \\
  --data '{
    "customerReferenceId": "BOL-2349",
    "pickup":  { "location": { "addressLine1": "618 S Racine Ave" } },
    "dropoff": { "location": { "addressLine1": "4341 W Van Buren St" } },
    "items": [{ "name": "Copper pipe bundle", "quantity": 12 }]
  }'

# Book the quote you like
curl --request POST ${baseUrl}/api/payloads/pay_8fk32/book \\
  --header 'Curri-Version: ${CURRI_VERSION}' \\
  --data '{ "quoteId": "qt_04af7" }'`

const Note = ({ children }: { children: string }) => (
  <span style={{ color: CONSOLE.muted }}>{children}</span>
)

const Flag = ({ children }: { children: string }) => (
  <span style={{ color: CONSOLE.subdued }}>{children}</span>
)

const Str = ({ children }: { children: string }) => (
  <span style={{ color: CONSOLE.accent }}>{children}</span>
)

const Snippet = ({ baseUrl }: { baseUrl: string }) => (
  <pre
    style={{
      margin: 0,
      padding: '16px 20px',
      height: 346,
      overflow: 'auto',
      boxSizing: 'border-box',
      fontFamily: 'var(--curri-font-mono)',
      fontSize: 11.5,
      lineHeight: 1.75,
      color: CONSOLE.text,
    }}
  >
    <Note># Create a payload — quotes come back embedded</Note>
    {`\ncurl ${baseUrl}/api/payloads \\\n  `}
    <Flag>--request</Flag>
    {' POST \\\n  '}
    <Flag>--header</Flag> <Str>{`'Curri-Version: ${CURRI_VERSION}'`}</Str>
    {' \\\n  '}
    <Flag>--header</Flag> <Str>{"'Authorization: Bearer $CURRI_TOKEN'"}</Str>
    {' \\\n  '}
    <Flag>--data</Flag>{' '}
    <Str>
      {`'{
    "customerReferenceId": "BOL-2349",
    "pickup":  { "location": { "addressLine1": "618 S Racine Ave" } },
    "dropoff": { "location": { "addressLine1": "4341 W Van Buren St" } },
    "items": [{ "name": "Copper pipe bundle", "quantity": 12 }]
  }'`}
    </Str>
    {'\n\n'}
    <Note># Book the quote you like</Note>
    {'\ncurl '}
    <Flag>--request</Flag>
    {` POST \\\n  ${baseUrl}/api/payloads/pay_8fk32/book \\\n  `}
    <Flag>--header</Flag> <Str>{`'Curri-Version: ${CURRI_VERSION}'`}</Str>
    {' \\\n  '}
    <Flag>--data</Flag> <Str>{`'{ "quoteId": "qt_04af7" }'`}</Str>
  </pre>
)

const WebhookFeed = ({ events }: { events: FeedEvent[] }) => (
  <div style={{ borderTop: `1px solid ${CONSOLE.line}` }}>
    <div style={{ display: 'flex', alignItems: 'center', padding: '12px 20px 0' }}>
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: '0.08em',
          color: CONSOLE.muted,
        }}
      >
        WEBHOOK DELIVERIES
      </span>
      <span style={{ flex: 1 }} />
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <span
          style={{
            width: 5,
            height: 5,
            borderRadius: '50%',
            background: CONSOLE.accent,
          }}
        />
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.07em',
            color: CONSOLE.accent,
          }}
        >
          LIVE
        </span>
      </span>
    </div>
    {/* `column-reverse` puts the newest event on top while the array stays
        append-only; the fixed height clips the tail rather than growing the
        console. */}
    <div
      style={{
        display: 'flex',
        flexDirection: 'column-reverse',
        justifyContent: 'flex-end',
        gap: 10,
        height: 92,
        overflow: 'hidden',
        padding: '12px 20px 14px',
        boxSizing: 'border-box',
      }}
    >
      {events.map(event => (
        <div
          key={event.id}
          className="landing-event"
          style={{ display: 'flex', alignItems: 'center', gap: 9 }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: event.dot,
              flex: 'none',
            }}
          />
          <span
            style={{
              fontFamily: 'var(--curri-font-mono)',
              fontSize: 12,
              fontWeight: 500,
              color: CONSOLE.textBright,
            }}
          >
            {event.name}
          </span>
          <span style={{ flex: 1 }} />
          <span
            style={{
              fontFamily: 'var(--curri-font-mono)',
              fontSize: 11,
              color: CONSOLE.muted,
            }}
          >
            {event.meta}
          </span>
          <span
            style={{
              fontFamily: 'var(--curri-font-mono)',
              fontSize: 11,
              color: CONSOLE.dim,
            }}
          >
            {event.time}
          </span>
        </div>
      ))}
    </div>
  </div>
)

const COPY_FEEDBACK_MS = 1600

export const CodeConsole = () => {
  const { config } = useAuth()
  const events = useCyclingFeed(makeFeedEvent)
  const [copied, setCopied] = useState(false)
  const copyTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => () => clearTimeout(copyTimer.current), [])

  const copy = () => {
    // Clipboard access can be denied (permissions policy, insecure context);
    // a marketing snippet is not worth surfacing that as an error.
    navigator.clipboard?.writeText(curlSnippet(config.publicUrl)).catch(() => {})
    setCopied(true)
    clearTimeout(copyTimer.current)
    copyTimer.current = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS)
  }

  return (
    <div
      className="landing-up"
      style={{
        flex: '1 1 480px',
        minWidth: 'min(440px, 100%)',
        maxWidth: 620,
        animationDelay: '0.1s',
      }}
    >
      <div
        style={{
          background: CONSOLE.surface,
          border: `1px solid ${CONSOLE.line}`,
          borderRadius: 14,
          boxShadow: 'var(--shadow-elevation-overlay)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '12px 12px 11px',
            borderBottom: `1px solid ${CONSOLE.line}`,
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              height: 29,
              padding: '0 12px',
              borderRadius: 999,
              border: '1px solid rgba(250, 250, 249, 0.18)',
              background: 'rgba(250, 250, 249, 0.13)',
              color: CONSOLE.textStrong,
              fontSize: 12.5,
              fontWeight: 600,
            }}
          >
            cURL
          </span>
          <span style={{ flex: 1 }} />
          <span
            style={{
              fontFamily: 'var(--curri-font-mono)',
              fontSize: 11,
              color: CONSOLE.muted,
            }}
          >
            POST /api/payloads
          </span>
          <button
            type="button"
            onClick={copy}
            aria-label="Copy code"
            className="landing-console-copy"
            style={{
              width: 28,
              height: 28,
              marginLeft: 4,
              borderRadius: 7,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              background: 'transparent',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: copied ? CONSOLE.accent : CONSOLE.subdued,
            }}
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </button>
        </div>

        <Snippet baseUrl={config.publicUrl} />

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '11px 20px',
            borderTop: `1px solid ${CONSOLE.line}`,
          }}
        >
          <span
            className="landing-pulse-dot"
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: CONSOLE.live,
            }}
          />
          <span
            style={{
              fontFamily: 'var(--curri-font-mono)',
              fontSize: 12,
              color: CONSOLE.accent,
            }}
          >
            201 Created
          </span>
          <span
            style={{
              fontFamily: 'var(--curri-font-mono)',
              fontSize: 12,
              color: CONSOLE.muted,
            }}
          >
            · 4 quotes embedded
          </span>
          <span style={{ flex: 1 }} />
          <span
            style={{
              fontFamily: 'var(--curri-font-mono)',
              fontSize: 11,
              color: CONSOLE.muted,
            }}
          >
            312 ms
          </span>
        </div>

        <WebhookFeed events={events} />
      </div>
    </div>
  )
}
